import type { ExperienceItem, TrackExperienceMetadata } from '@/types/abtest'
import { pushConvertActionToDatalayer, pushConvertExperimentsToDataLayer } from '@/utils/abtest'

export type TrackExperienceOptions = {
  experienceKey: string
} & TrackExperienceMetadata

export type TrackExperienceInput = string | TrackExperienceOptions

export type TrackOptions = {
  experiences?: TrackExperienceInput[]
}

type PendingTrack = {
  variation: ExperienceItem
  metadata?: TrackExperienceMetadata
}

const pendingByExperienceKey = new Map<string, PendingTrack>()
let listenerRegistered = false
let flushPendingTracksFn: (() => Promise<void>) | null = null

function normalizeTrackExperienceInput(entry: TrackExperienceInput): TrackExperienceOptions {
  return typeof entry === 'string' ? { experienceKey: entry } : entry
}

function trackExperienceOptionsToMetadata(entry: TrackExperienceOptions): TrackExperienceMetadata {
  const { experienceKey: _k, ...rest } = entry
  return {
    ...rest
  }
}

function hasCookieConsentCookie(): boolean {
  if (!import.meta.client) {
    return false
  }

  return /(?:^|; )CookieConsent=/.test(document.cookie)
}

function onCookiebotConsentReady() {
  void flushPendingTracksFn?.()
}

function ensureConsentListener() {
  if (!import.meta.client) {
    return
  }

  if (!listenerRegistered) {
    listenerRegistered = true
    window.addEventListener('CookiebotOnConsentReady', onCookiebotConsentReady)
    window.addEventListener('CookiebotOnAccept', onCookiebotConsentReady)
    window.addEventListener('CookiebotOnDecline', onCookiebotConsentReady)
  }

  // Consent may already be ready if CookieConsent was set before the listener was registered
  if (hasCookieConsentCookie()) {
    onCookiebotConsentReady()
  }
}

export const useConvert = () => {
  const route = useRoute()
  const authStore = useAuthStore()
  const variationsCookie = useCookie<ExperienceItem[]>('ota-conv-ab')
  const visitorIdCookie = useCookie('ota-conv-context')

  const pushCurrentVisitorExperimentToDataLayer = (
    experienceKey: string,
    metadata?: TrackExperienceMetadata
  ) => {
    const experiences = variationsCookie.value || []
    const visitorId = visitorIdCookie.value || ''
    const userId = authStore.authId

    if (!visitorId) {
      return
    }

    const matchingExperience = experiences.find((experience) => experience?.experienceKey === experienceKey)

    if (!matchingExperience) {
      return
    }

    pushConvertExperimentsToDataLayer(
      [matchingExperience],
      visitorId,
      userId,
      metadata?.memberId
    )
  }

  const pushCurrentVisitorExperimentInteractionToDataLayer = (
    experienceKey: string,
    interaction: string,
    metadata?: TrackExperienceMetadata
  ) => {
    const experiences = variationsCookie.value || []
    const visitorId = visitorIdCookie.value || ''
    const userId = authStore.authId

    if (!visitorId) {
      return
    }

    const matchingExperience = experiences.find((experience) => experience?.experienceKey === experienceKey)

    if (!matchingExperience) {
      return
    }

    pushConvertActionToDatalayer(interaction, matchingExperience, visitorId, userId, metadata)
  }

  const flushPendingTracks = async () => {
    if (!pendingByExperienceKey.size) {
      return
    }

    const pending = Array.from(pendingByExperienceKey.values())
    pendingByExperienceKey.clear()

    await Promise.all(
      pending.map(({ variation }) =>
        $fetch('/nuxt-api/convert/track', {
          method: 'POST',
          body: { variation }
        }).catch((error) => {
          logError(error)
        })
      )
    )

    for (const { variation, metadata } of pending) {
      pushCurrentVisitorExperimentToDataLayer(variation.experienceKey, metadata)
    }
  }

  flushPendingTracksFn = flushPendingTracks

  const track = async (options: TrackOptions = {}): Promise<ExperienceItem[] | null> => {
    const experienceEntries: TrackExperienceOptions[] = (options.experiences ?? []).map(
      normalizeTrackExperienceInput
    )

    const experienceKeys = experienceEntries.map((e) => e.experienceKey)

    if (!experienceKeys.length) {
      return null
    }

    const metaByExperienceKey = new Map<string, TrackExperienceOptions>()
    for (const entry of experienceEntries) {
      metaByExperienceKey.set(entry.experienceKey, entry)
    }

    const variations = variationsCookie.value || []
    const matchedVariations = variations.filter((v) => experienceKeys.includes(v.experienceKey))

    if (!matchedVariations.length) {
      return null
    }

    for (const variation of matchedVariations) {
      const entry = metaByExperienceKey.get(variation.experienceKey)
      pendingByExperienceKey.set(variation.experienceKey, {
        variation,
        metadata: entry ? trackExperienceOptionsToMetadata(entry) : undefined
      })
    }

    ensureConsentListener()

    return matchedVariations
  }

  const refreshConvertVariations = async () => {
    try {
      await $fetch('/nuxt-api/convert/refresh', {
        method: 'POST',
        body: {
          pathname: route.path
        }
      })
    } catch (error) {
      logError(error)
    }
  }

  return {
    track,
    refreshConvertVariations,
    pushCurrentVisitorExperimentToDataLayer,
    pushCurrentVisitorExperimentInteractionToDataLayer
  }
}
